/**
 * RFM Matrix Visualization Component
 *
 * Displays a 10x10 heat map matrix showing:
 * - Customer distribution across RFM score combinations
 * - Color-coded cells based on customer density
 * - Interactive cell selection
 * - Segment boundary overlays
 *
 * Typically shows Recency (Y-axis) vs Frequency (X-axis)
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/frontend/components/ui/card";
import { Badge } from "@/frontend/components/ui/badge";
import type { CustomerRFMData } from "@/shared/types/rfm-analytics";
import { MATRIX_TRANSLATIONS, CUSTOMER_LIST_TRANSLATIONS } from "@/shared/lib/rfm-translations";

interface RFMMatrixProps {
  customers: CustomerRFMData[];
  xAxis?: 'frequency' | 'monetary';
  yAxis?: 'recency' | 'frequency';
  onCellClick?: (xScore: number, yScore: number) => void;
}

export function RFMMatrix({
  customers,
  xAxis = 'frequency',
  yAxis = 'recency',
  onCellClick
}: RFMMatrixProps) {

  // Build matrix data: count customers in each cell
  const buildMatrixData = () => {
    const matrix: number[][] = Array(10).fill(0).map(() => Array(10).fill(0));

    customers.forEach(customer => {
      let x: number, y: number;

      // X-axis score
      if (xAxis === 'frequency') {
        x = customer.rfmScore.frequencyScore - 1; // Convert 1-10 to 0-9
      } else {
        x = customer.rfmScore.monetaryScore - 1;
      }

      // Y-axis score
      if (yAxis === 'recency') {
        y = customer.rfmScore.recencyScore - 1; // Convert 1-10 to 0-9
      } else {
        y = customer.rfmScore.frequencyScore - 1;
      }

      // Ensure within bounds
      x = Math.max(0, Math.min(9, x));
      y = Math.max(0, Math.min(9, y));

      matrix[y][x]++;
    });

    return matrix;
  };

  const matrixData = buildMatrixData();
  const maxCount = Math.max(...matrixData.flat());

  // Get color intensity based on count
  const getCellColor = (count: number): string => {
    if (count === 0) return 'bg-gray-50';

    const intensity = count / maxCount;

    if (intensity >= 0.75) return 'bg-green-600 text-white';
    if (intensity >= 0.5) return 'bg-green-500 text-white';
    if (intensity >= 0.25) return 'bg-green-400';
    return 'bg-green-200';
  };

  // Get axis label with Thai translation
  const getAxisLabel = (axis: 'recency' | 'frequency' | 'monetary'): string => {
    switch (axis) {
      case 'recency': return MATRIX_TRANSLATIONS.recency;
      case 'frequency': return MATRIX_TRANSLATIONS.frequency;
      case 'monetary': return MATRIX_TRANSLATIONS.monetary;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{MATRIX_TRANSLATIONS.title}</CardTitle>
          <Badge variant="outline" className="text-xs">
            {customers.length} {CUSTOMER_LIST_TRANSLATIONS.customers}
          </Badge>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          {getAxisLabel(yAxis)} (↓) vs {getAxisLabel(xAxis)} (→)
        </p>
      </CardHeader>

      <CardContent>
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full">
            {/* Matrix Grid */}
            <div className="flex">
              {/* Y-axis label - fixed width container */}
              <div className="w-8 flex-shrink-0 flex flex-col justify-center items-center">
                <div className="transform -rotate-90 whitespace-nowrap text-xs font-medium text-gray-600">
                  {getAxisLabel(yAxis)} Score →
                </div>
              </div>

              {/* Y-axis numbers */}
              <div className="flex flex-col gap-1 w-6 flex-shrink-0">
                {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map((score) => (
                  <div
                    key={score}
                    className="h-8 w-6 flex items-center justify-center text-xs font-medium text-gray-600"
                  >
                    {score}
                  </div>
                ))}
              </div>

              {/* Matrix cells */}
              <div className="flex flex-col gap-1 ml-1">
                {/* Reverse Y-axis for display (10 at top, 1 at bottom) */}
                {[9, 8, 7, 6, 5, 4, 3, 2, 1, 0].map((y) => (
                  <div key={y} className="flex gap-1">
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((x) => {
                      const count = matrixData[y][x];
                      const cellColor = getCellColor(count);

                      return (
                        <button
                          key={`${x}-${y}`}
                          className={`h-8 w-8 flex items-center justify-center text-xs font-medium rounded transition-all hover:ring-2 hover:ring-blue-500 ${cellColor}`}
                          onClick={() => onCellClick?.(x + 1, y + 1)}
                          title={`${getAxisLabel(xAxis)}: ${x + 1}, ${getAxisLabel(yAxis)}: ${y + 1}\n${count} customers`}
                        >
                          {count > 0 ? count : ''}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* X-axis numbers */}
            <div className="flex mt-1">
              {/* Spacer for Y-axis label - matches w-8 above */}
              <div className="w-8 flex-shrink-0" />
              {/* Spacer for Y-axis numbers - matches w-6 above */}
              <div className="w-6 flex-shrink-0" />
              {/* Numbers aligned with cells - must match cell width (w-8) and gap-1, plus ml-1 offset */}
              <div className="flex gap-1 ml-1">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
                  <div
                    key={score}
                    className="w-8 flex items-center justify-center text-xs font-medium text-gray-600"
                  >
                    {score}
                  </div>
                ))}
              </div>
            </div>

            {/* X-axis label */}
            <div className="flex mt-2">
              {/* Spacer for Y-axis label - matches w-8 above */}
              <div className="w-8 flex-shrink-0" />
              {/* Spacer for Y-axis numbers - matches w-6 above */}
              <div className="w-6 flex-shrink-0" />

              {/* Label container matching cell grid width: 10 cells × 32px + 9 gaps × 4px = 356px */}
              <div className="ml-1" style={{ width: 'calc(10 * 2rem + 9 * 0.25rem)' }}>
                <div className="text-center text-xs font-medium text-gray-600">
                  {getAxisLabel(xAxis)} →
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-6 pt-4 border-t">
          <p className="text-xs font-medium text-gray-600 mb-2">{MATRIX_TRANSLATIONS.customerDensity}</p>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 bg-gray-50 border rounded" />
              <span className="text-xs text-gray-600">{MATRIX_TRANSLATIONS.zero}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 bg-green-200 rounded" />
              <span className="text-xs text-gray-600">{MATRIX_TRANSLATIONS.low}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 bg-green-400 rounded" />
              <span className="text-xs text-gray-600">{MATRIX_TRANSLATIONS.medium}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 bg-green-500 rounded" />
              <span className="text-xs text-gray-600">{MATRIX_TRANSLATIONS.high}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 bg-green-600 rounded" />
              <span className="text-xs text-gray-600">{MATRIX_TRANSLATIONS.veryHigh}</span>
            </div>
          </div>
        </div>

        {/* Matrix info */}
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-xs text-gray-700">
            <strong>{MATRIX_TRANSLATIONS.tip}</strong> {MATRIX_TRANSLATIONS.tipDescription}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Simplified mini matrix for overview displays
 */
interface RFMMatrixMiniProps {
  customers: CustomerRFMData[];
}

export function RFMMatrixMini({ customers }: RFMMatrixMiniProps) {
  // Simplified 5x5 grid (group scores into bins)
  const buildSimplifiedMatrix = () => {
    const matrix: number[][] = Array(5).fill(0).map(() => Array(5).fill(0));

    customers.forEach(customer => {
      const x = Math.floor((customer.rfmScore.frequencyScore - 1) / 2); // Group into 5 bins
      const y = Math.floor((customer.rfmScore.recencyScore - 1) / 2);

      const xBin = Math.max(0, Math.min(4, x));
      const yBin = Math.max(0, Math.min(4, y));

      matrix[yBin][xBin]++;
    });

    return matrix;
  };

  const matrixData = buildSimplifiedMatrix();
  const maxCount = Math.max(...matrixData.flat());

  const getCellColor = (count: number): string => {
    if (count === 0) return 'bg-gray-100';
    const intensity = count / maxCount;
    if (intensity >= 0.66) return 'bg-green-500';
    if (intensity >= 0.33) return 'bg-green-300';
    return 'bg-green-100';
  };

  return (
    <div className="inline-block">
      <div className="flex flex-col gap-0.5">
        {[4, 3, 2, 1, 0].map((y) => (
          <div key={y} className="flex gap-0.5">
            {[0, 1, 2, 3, 4].map((x) => (
              <div
                key={`${x}-${y}`}
                className={`h-4 w-4 rounded-sm ${getCellColor(matrixData[y][x])}`}
                title={`${matrixData[y][x]} customers`}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
